
import { computed, defineComponent, getCurrentInstance, PropType, ref, watch } from 'vue'
import { useBillingStore } from '../../../../store/stores/billing'
import { useProject } from '../../../../hooks/useProject'
import { useUser } from '@/hooks/useUser'
import api from '../../../../api/api'
import SpinnerLoader from '../../../../components/SpinnerLoader.vue'
import { declOfNum, openInCurrentTab } from '../../../../utils'
import { CurrentPrice, MarketplaceIntegration } from '../../../../types/marketplace'
import { SolvePaymentParams } from '@api/marketplace'
import { formatOrderPromocodes } from '@/helpers/billing/promocodeDiscountLabel'

interface ModuleInstallPayload {
    projectId?: string
    marketplaceIntegrationId?: string
    subscriptionPeriod?: number | string
}

type PaymentPrice = CurrentPrice & {
    needToPay?: number
}

interface SolvePaymentResult {
    paymentPayloadId?: number
    price?: PaymentPrice
}

export default defineComponent({
    components: { SpinnerLoader },
    props: {
        integration: {
            type: Object as PropType<MarketplaceIntegration>,
            required: true,
        },
        integrationInstallPayload: {
            type: Object as PropType<ModuleInstallPayload>,
        },
    },
    setup(props) {
        const root = getCurrentInstance()!.proxy
        const billingStore = useBillingStore()
        const { project } = useProject()
        const modalLoading = ref(false)
        const loadError = ref('')
        const paymentLinkLoading = ref(false)
        const solvedPaymentData = ref<SolvePaymentResult | null>(null)
        const paymentPayloadId = ref<number>()
        const { user } = useUser()

        const modalOpened = computed({
            get: () => billingStore.paymentModal.opened,
            set: (v: boolean) => billingStore.setPaymentModalOpened(v),
        })
        const renewal = computed({
            get: () => billingStore.paymentModal.renewal,
            set: (v: boolean) => billingStore.setPaymentModalOpened(billingStore.paymentModal.opened, v),
        })

        const paymentPrice = computed(() => solvedPaymentData.value?.price)
        const hasPaymentData = computed(() => Boolean(paymentPrice.value))
        const canPay = computed(() => hasPaymentData.value && Boolean(paymentPayloadId.value) && !modalLoading.value)

        const currentTariffData = computed(() => billingStore.getCurrentTariff(project.value?.id))
        const selectedTariff = computed(() => props.integration)
        const installPayload = computed(
            (): ModuleInstallPayload => (props.integrationInstallPayload ?? {}) as ModuleInstallPayload
        )

        const subscriptionPeriod = computed(() => {
            const fromPayload = installPayload.value.subscriptionPeriod
            if (fromPayload != null && fromPayload !== '') return String(fromPayload)
            if (billingStore.selectedSubscriptionPeriod) return String(billingStore.selectedSubscriptionPeriod)
            return '1'
        })

        const formattedCurrentTariffendOfUse = computed(() => {
            if (!currentTariffData.value?.endOfUse) return ''
            return new Date(currentTariffData.value.endOfUse).toLocaleDateString()
        })
        const formattedUnusedDiscount = computed(() => {
            if (paymentPrice.value?.unusedDiscount == null) return ''
            return billingStore.formatPrice(paymentPrice.value.unusedDiscount)
        })
        const refundDescription = computed(() => {
            if (paymentPrice.value?.toRefund == null) return ''
            return `${billingStore.formatPrice(paymentPrice.value.toRefund)} вернется вам на балланс`
        })
        const selectedTariffFormattedNeedToPay = computed(() => {
            const amount = paymentPrice.value?.needToPay ?? paymentPrice.value?.toPay
            if (amount == null) return '—'
            return billingStore.formatPrice(amount)
        })
        const selectedTariffFormattedRegularPaymentPrice = computed(() => {
            const regularPay =
                currentTariffData.value?.price?.regularPay ?? paymentPrice.value?.regularPay
            if (regularPay == null) return '—'
            return billingStore.formatPrice(regularPay)
        })
        const formattedResultPrice = computed(() => {
            if (paymentPrice.value?.toPay == null) return '—'
            return billingStore.formatPrice(paymentPrice.value.toPay)
        })
        const formattedBalanceOfAccount = computed(() => {
            if (paymentPrice.value?.balanceOfAccount == null) return ''
            return billingStore.formatPrice(paymentPrice.value.balanceOfAccount)
        })
        const selectedTariffPaymentDescription = computed(() => {
            const periodKey = subscriptionPeriod.value
            const firstPayment = selectedTariff.value?.complexPrice?.monthPeriod?.[periodKey]?.firstPayment
            if (!firstPayment || !paymentPrice.value) return ''

            const firstPaymentIsSpecial = firstPayment.months !== Number(periodKey)
            if (firstPaymentIsSpecial && paymentPrice.value.isFirstPurchase) {
                const regularPaymentFormattedDate = paymentPrice.value.regularPayAfter
                    ? new Date(paymentPrice.value.regularPayAfter).toLocaleDateString()
                    : ''
                return `Первое списание за ${firstPayment.months} ${declOfNum(firstPayment.months, [
                    'месяц',
                    'месяца',
                    'месяцев',
                ])}, далее с ${regularPaymentFormattedDate} ежемесячно по ${billingStore.formatPrice(
                    paymentPrice.value.regularPay
                )}`
            }
            return ''
        })
        const formattedTodayDate = computed(() => new Date().toLocaleDateString())

        const promoName = computed(() => {
            const promocodeData = paymentPrice.value?.promocodeData
            return formatOrderPromocodes(promocodeData)
        })

        const formattedPromoDiscount = computed(() => {
            const discount = paymentPrice.value?.promocodeData?.promocodeDiscount
            if (discount == null || discount === '') return ''
            return billingStore.formatPrice(Number(discount))
        })

        const resetPaymentState = () => {
            solvedPaymentData.value = null
            paymentPayloadId.value = undefined
            loadError.value = ''
        }

        watch(modalOpened, async opened => {
            if (opened) {
                await initData()
            } else {
                resetPaymentState()
                modalLoading.value = false
                paymentLinkLoading.value = false
            }
        })

        const closeModal = () => {
            billingStore.setAddChannelContext(null)
            billingStore.setPaymentModalOpened(false)
            resetPaymentState()
        }

        const initData = async () => {
            if (!project.value?.id || !user.value?.sub || !selectedTariff.value?.id) {
                loadError.value = 'Не удалось подготовить оплату. Проверьте проект и попробуйте снова.'
                modalLoading.value = false
                return
            }

            modalLoading.value = true
            loadError.value = ''
            solvedPaymentData.value = null
            paymentPayloadId.value = undefined

            const solveMethod = renewal.value
                ? api.marketplace.solveRenewalSubscription
                : api.marketplace.solvePayment

            const solvePaymentParams: SolvePaymentParams = {
                projectId: installPayload.value.projectId || project.value.id,
                marketplaceIntegrationId:
                    installPayload.value.marketplaceIntegrationId || selectedTariff.value.id,
                subscriptionPeriod: subscriptionPeriod.value,
                userId: user.value.sub,
            }

            try {
                const { data, error } = await solveMethod(solvePaymentParams)
                const result = data as unknown as SolvePaymentResult
                if (error || !result?.price) {
                    loadError.value = 'Не удалось загрузить данные о платеже. Попробуйте ещё раз или закройте окно.'
                    root.$store.dispatch('callNotify', 'Ошибка при загрузке данных о платеже')
                    return
                }
                solvedPaymentData.value = result
                paymentPayloadId.value = result.paymentPayloadId
            } catch {
                loadError.value = 'Не удалось загрузить данные о платеже. Попробуйте ещё раз или закройте окно.'
                root.$store.dispatch('callNotify', 'Ошибка при загрузке данных о платеже')
            } finally {
                modalLoading.value = false
            }
        }

        const makePayment = async () => {
            if (!canPay.value || !project.value?.id || !selectedTariff.value?.id) return

            paymentLinkLoading.value = true
            try {
                const { data, error } = await api.marketplace.installIntegration({
                    paymentPayloadId: paymentPayloadId.value,
                    integrationId: selectedTariff.value.id,
                    projectId: project.value.id,
                    subscriptionPeriod: subscriptionPeriod.value,
                })

                if (error) {
                    root.$store.dispatch('callNotify', 'Ошибка при попытке оплаты')
                    return
                }
                if (data?.status === 'needPayment' && data.paymentLink) {
                    openInCurrentTab(data.paymentLink)
                }
                if (data?.status === 'installed') {
                    root.$store.dispatch('callNotify', 'Подписка успешно оформлена')
                    window.location.reload()
                }
            } catch {
                root.$store.dispatch('callNotify', 'Ошибка при попытке оплаты')
            } finally {
                paymentLinkLoading.value = false
            }
        }

        return {
            billingStore,
            modalOpened,
            renewal,
            selectedTariff,
            formattedTodayDate,
            modalLoading,
            loadError,
            hasPaymentData,
            canPay,
            paymentPrice,
            selectedTariffFormattedNeedToPay,
            selectedTariffFormattedRegularPaymentPrice,
            selectedTariffPaymentDescription,
            currentTariffData,
            formattedCurrentTariffendOfUse,
            formattedUnusedDiscount,
            refundDescription,
            closeModal,
            initData,
            formattedResultPrice,
            makePayment,
            paymentLinkLoading,
            solvedPaymentData,
            promoName,
            formattedPromoDiscount,
            formattedBalanceOfAccount,
        }
    },
})
